/**
 * src/lib/data/rbac.ts — view-owner write authorization helpers.
 *
 * Phase 5.5b — model revised. Helpers check `level` + `isAdmin` instead
 * of the deprecated `role` enum. Open reads per §1.4 (no canReadX
 * helpers — every viewer reads everything). Writes per §1.5.
 *
 * Trust model: frontend fast-path. The worker re-derives ownership
 * server-side. If this file and the worker disagree, the worker wins.
 */
import type { User } from "./types";

export class PermissionDeniedError extends Error {
  resource: string;
  constructor(viewer: User, resource: string) {
    super(
      `User ${viewer.id} (level=${viewer.level} isAdmin=${viewer.isAdmin}) ` +
      `cannot write ${resource} — view-owner gate failed.`
    );
    this.name = "PermissionDeniedError";
    this.resource = resource;
  }
}

// ─── Owner check helpers (§1.5 matrix, Phase 5.5b shape) ────────────────────

/** Self + admin: edit a user's display fields (name, email, label). */
export function canWriteUserDisplayFields(viewer: User, userId: string): boolean {
  if (!viewer.active) return false;
  return viewer.id === userId || viewer.isAdmin;
}

/** Admin only: edit level / marketId / hubspotOwnerId / commissionPlanRef / isAdmin / active. */
export function canWriteUserMetadata(viewer: User): boolean {
  return viewer.active && viewer.isAdmin;
}

/**
 * AE-month target/actual: only the AE themselves owns their per-month numbers.
 * MID-level users (GTM Lead, Sales Lead) do NOT override an AE — Marco's call
 * (option A from architecture §1.5 #2). To re-allow override later: change
 * this to `(viewer.id === aeId) || (viewer.level === "MID" && viewer.marketId === marketIdOfAe)`.
 */
export function canWriteAEMonth(viewer: User, aeId: string): boolean {
  if (!viewer.active) return false;
  return viewer.id === aeId;
}

/**
 * Market month-level forecast (gtmTarget, partnerMrr, churnAbs, seasonality,
 * scenarios per market): MID-level user with this market in their list.
 * Phase 5.5c — marketIds is an array so a MID can own multiple markets.
 */
export function canWriteMarketForecast(viewer: User, marketId: string): boolean {
  if (!viewer.active) return false;
  return viewer.level === "MID" && Array.isArray(viewer.marketIds) && viewer.marketIds.includes(marketId);
}

/**
 * Scenario selection (active scenario) — same owner as market forecast.
 * Shared across the market (§1.5).
 */
export function canWriteActiveScenario(viewer: User, marketId: string): boolean {
  return canWriteMarketForecast(viewer, marketId);
}

/** View password rotation: a MID who has this market OR any admin. */
export function canRotateViewPassword(viewer: User, marketId: string): boolean {
  if (!viewer.active) return false;
  if (viewer.isAdmin) return true;
  return viewer.level === "MID" && Array.isArray(viewer.marketIds) && viewer.marketIds.includes(marketId);
}

/** Edit password rotation: admin only. */
export function canRotateEditPassword(viewer: User): boolean {
  return viewer.active && viewer.isAdmin;
}

// ─── assert variants — throw on deny ────────────────────────────────────────

export function assertCanWriteUserDisplayFields(viewer: User, userId: string): void {
  if (!canWriteUserDisplayFields(viewer, userId)) {
    throw new PermissionDeniedError(viewer, `user:${userId}:display`);
  }
}

export function assertCanWriteUserMetadata(viewer: User): void {
  if (!canWriteUserMetadata(viewer)) {
    throw new PermissionDeniedError(viewer, "user:*:metadata");
  }
}

export function assertCanWriteAEMonth(viewer: User, aeId: string, monthKey: string): void {
  if (!canWriteAEMonth(viewer, aeId)) {
    throw new PermissionDeniedError(viewer, `ae-month:${aeId}:${monthKey}`);
  }
}

export function assertCanWriteMarketForecast(viewer: User, marketId: string, monthKey: string): void {
  if (!canWriteMarketForecast(viewer, marketId)) {
    throw new PermissionDeniedError(viewer, `market:${marketId}:${monthKey}`);
  }
}

export function assertCanRotateViewPassword(viewer: User, marketId: string): void {
  if (!canRotateViewPassword(viewer, marketId)) {
    throw new PermissionDeniedError(viewer, `auth:view-password:${marketId}`);
  }
}

export function assertCanRotateEditPassword(viewer: User): void {
  if (!canRotateEditPassword(viewer)) {
    throw new PermissionDeniedError(viewer, "auth:edit-password");
  }
}
